import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FileText, Upload, Download, Eye, Trash2, Plus, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { storageService, MAX_DOCUMENT_SIZE_BYTES } from '@/services/storageService';
import type { DocumentRow } from '@/types';

interface DocumentsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: {
    id: string;
    provider: {
      nombre: string;
      tipo: string;
    };
    descripcion_servicio: string;
  } | null;
}

const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const DocumentsModal: React.FC<DocumentsModalProps> = ({
  open,
  onOpenChange,
  provider,
}) => {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const proveedorId = provider?.id;
  const documentsQueryKey = ['documents', { proveedorId }] as const;

  const {
    data: documents = [],
    isLoading,
    isError,
  } = useQuery<DocumentRow[]>({
    queryKey: documentsQueryKey,
    queryFn: () => storageService.listDocuments({ proveedorId }),
    enabled: open && !!proveedorId,
  });

  const uploadMutation = useMutation({
    mutationFn: (input: { file: File; tipo: string }) =>
      storageService.uploadDocument({
        file: input.file,
        proveedorId,
        tipo: input.tipo || undefined,
      }),
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: 'Documento subido',
        description: `${doc.nombre} se subió correctamente`,
      });
      setShowUploadForm(false);
      setSelectedFile(null);
      setDocumentType('');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Intenta de nuevo más tarde';
      toast({
        variant: 'destructive',
        title: 'Error al subir documento',
        description: message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => storageService.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast({
        title: 'Documento eliminado',
        description: 'El documento se eliminó correctamente',
      });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Intenta de nuevo más tarde';
      toast({
        variant: 'destructive',
        title: 'Error al eliminar documento',
        description: message,
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > MAX_DOCUMENT_SIZE_BYTES) {
      toast({
        variant: 'destructive',
        title: 'Archivo demasiado grande',
        description: 'El archivo es demasiado grande (máx 25 MB)',
      });
      e.target.value = '';
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Selecciona un archivo para subir',
      });
      return;
    }
    if (selectedFile.size > MAX_DOCUMENT_SIZE_BYTES) {
      toast({
        variant: 'destructive',
        title: 'Archivo demasiado grande',
        description: 'El archivo es demasiado grande (máx 25 MB)',
      });
      return;
    }
    uploadMutation.mutate({ file: selectedFile, tipo: documentType.trim() });
  };

  const openSignedUrl = async (doc: DocumentRow) => {
    try {
      const { url } = await storageService.getSignedUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Intenta de nuevo más tarde';
      toast({
        variant: 'destructive',
        title: 'Error al cargar documento',
        description: message,
      });
    }
  };

  const handleDelete = (doc: DocumentRow) => {
    if (confirm(`¿Eliminar "${doc.nombre}"? Esta acción no se puede deshacer.`)) {
      deleteMutation.mutate(doc.id);
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Documentos del Proveedor
          </DialogTitle>
          <DialogDescription>
            Gestiona los documentos asociados a este proveedor, como contratos, cotizaciones y facturas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Provider Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium">{provider.provider.nombre}</h3>
            <p className="text-sm text-gray-600">{provider.provider.tipo}</p>
            <p className="text-sm text-gray-600 mt-1">{provider.descripcion_servicio}</p>
          </div>

          {/* Upload Button */}
          <div className="flex justify-between items-center">
            <h4 className="font-medium">
              Documentos ({documents.length})
            </h4>
            <Button
              onClick={() => setShowUploadForm(!showUploadForm)}
              size="sm"
              disabled={uploadMutation.isPending}
            >
              <Plus className="w-4 h-4 mr-2" />
              Subir Documento
            </Button>
          </div>

          {/* Upload Form */}
          {showUploadForm && (
            <form onSubmit={handleUpload} className="bg-blue-50 p-4 rounded-lg space-y-4">
              <div>
                <Label htmlFor="document-file">Seleccionar Archivo</Label>
                <Input
                  id="document-file"
                  name="document-file"
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  required
                  disabled={uploadMutation.isPending}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tamaño máximo: 25 MB. Formatos: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
                </p>
              </div>
              <div>
                <Label htmlFor="document-type">Tipo de Documento (opcional)</Label>
                <Input
                  id="document-type"
                  name="document-type"
                  placeholder="Ej: Contrato, Cotización, Factura..."
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  disabled={uploadMutation.isPending}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={!selectedFile || uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowUploadForm(false);
                    setSelectedFile(null);
                    setDocumentType('');
                  }}
                  disabled={uploadMutation.isPending}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}

          {/* Documents List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Cargando documentos...
              </div>
            ) : isError ? (
              <div className="text-center py-8 text-red-600">
                <p>Error al cargar los documentos. Intenta de nuevo.</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No hay documentos subidos</p>
                <p className="text-sm">Haz clic en "Subir Documento" para agregar archivos</p>
              </div>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">{doc.nombre}</span>
                      {doc.tipo && (
                        <Badge variant="secondary" className="text-xs">
                          {doc.tipo}
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatDate(doc.created_at)} • {formatFileSize(doc.size_bytes)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSignedUrl(doc)}
                      title="Vista previa"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openSignedUrl(doc)}
                      title="Descargar"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(doc)}
                      title="Eliminar"
                      className="text-red-600 hover:text-red-700"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
